export type CodeReviewSeverity = "critical" | "major" | "minor" | "info";
export type CodeReviewOverallStatus = "ready" | "needs_work" | "blocked";

export interface CodeReviewFinding {
  severity: CodeReviewSeverity;
  area: string;
  issue: string;
  suggestion: string;
  optionalFixedSnippet?: string | null;
}

export interface IntegrationCodeReviewPayload {
  overallStatus: CodeReviewOverallStatus;
  summary?: string;
  findings: CodeReviewFinding[];
}

export type CertificationItemStatus = "pass" | "fail" | "na";
export type CertificationOverallReadiness = "ready" | "needs_work" | "blocked";

export interface CertificationChecklistItem {
  id: string;
  title: string;
  status: CertificationItemStatus;
  notes?: string;
  guideSectionRef?: string | null;
}

export interface IntegrationCertificationChecklistPayload {
  productUuid?: string;
  productName?: string;
  overallReadiness: CertificationOverallReadiness;
  passCount?: number;
  failCount?: number;
  items: CertificationChecklistItem[];
}

export interface IntegrationProductContextPayload {
  products?: Array<{
    uuid?: string;
    name?: string;
    apiUuids?: string[];
    developerGuideUuids?: string[];
  }>;
  selectedProductUuid?: string | null;
}

export interface IntegrationCodeSample {
  sectionId?: string;
  language: string;
  label?: string;
  code: string;
}

const CODE_REVIEW_RE = /```integration-code-review\s*([\s\S]*?)```/i;
const CERT_CHECKLIST_RE = /```integration-certification-checklist\s*([\s\S]*?)```/i;
const PRODUCT_CONTEXT_RE = /```integration-product-context\s*([\s\S]*?)```/i;
const CODE_SAMPLES_RE = /```integration-code-samples\s*([\s\S]*?)```/i;

function parseJsonBlock<T>(content: string, re: RegExp): T | null {
  const match = content.match(re);
  if (!match?.[1]) return null;
  try {
    return JSON.parse(match[1].trim()) as T;
  } catch {
    return null;
  }
}

export function parseIntegrationCodeReview(
  content: string,
): IntegrationCodeReviewPayload | null {
  return parseJsonBlock<IntegrationCodeReviewPayload>(content, CODE_REVIEW_RE);
}

export function parseIntegrationCertificationChecklist(
  content: string,
): IntegrationCertificationChecklistPayload | null {
  return parseJsonBlock<IntegrationCertificationChecklistPayload>(
    content,
    CERT_CHECKLIST_RE,
  );
}

export function parseIntegrationProductContext(
  content: string,
): IntegrationProductContextPayload | null {
  return parseJsonBlock<IntegrationProductContextPayload>(content, PRODUCT_CONTEXT_RE);
}

export function parseIntegrationCodeSamples(content: string): IntegrationCodeSample[] {
  const match = content.match(CODE_SAMPLES_RE);
  if (!match?.[1]) return [];
  try {
    const parsed = JSON.parse(match[1].trim()) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as IntegrationCodeSample[];
  } catch {
    return [];
  }
}

export function overallStatusLabel(status: CodeReviewOverallStatus): string {
  switch (status) {
    case "ready":
      return "Ready";
    case "needs_work":
      return "Needs work";
    case "blocked":
      return "Blocked";
    default:
      return status;
  }
}

export function readinessLabel(status: CertificationOverallReadiness): string {
  switch (status) {
    case "ready":
      return "Ready for review";
    case "needs_work":
      return "Needs work";
    case "blocked":
      return "Blocked";
    default:
      return status;
  }
}

export function severityColor(
  severity: CodeReviewSeverity,
): "error" | "warning" | "info" | "default" {
  switch (severity) {
    case "critical":
      return "error";
    case "major":
      return "warning";
    case "minor":
      return "info";
    default:
      return "default";
  }
}
